import {
  Card,
  CardContent,
  DataTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Typography,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import {
  getDashboardMyApproachingStatutesColumns,
  type DashboardApproachingStatuteRow,
} from "@/components/ui/DashboardMyApproachingStatutesCard/dashboard-my-approaching-statutes-columns";
import { searchAllMattersQueryOptions } from "@/lib/data/react-query/matters/search-matters";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import { resolveReportPrimaryStatute } from "@/lib/utils/matters/report-statute-utils";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

const APPROACHING_WINDOW_OPTIONS = [30, 60, 90, 120] as const;
const DEFAULT_APPROACHING_WINDOW_DAYS = 90;

export const DashboardMyApproachingStatutesCard = () => {
  const navigate = useNavigate();
  const { currentUser } = useUser();
  const [selectedWindowDays, setSelectedWindowDays] = useState(
    DEFAULT_APPROACHING_WINDOW_DAYS,
  );

  const columns = useMemo(() => getDashboardMyApproachingStatutesColumns(), []);
  const { data, isLoading, error } = useQuery(
    searchAllMattersQueryOptions({
      userId: currentUser.id,
      statusCategories: ["pre-litigation", "litigation"],
    }),
  );

  const rows = useMemo<DashboardApproachingStatuteRow[]>(() => {
    const windowEndDate = dayjs().startOf("day").add(selectedWindowDays, "day");

    return (data?.data ?? [])
      .map<DashboardApproachingStatuteRow>((matter) => {
        const statute = resolveReportPrimaryStatute(
          matter.statutesOfLimitations?.map((item) => ({
            ...item,
            date: item.date,
            description: item.description ?? undefined,
          })) ?? [],
        );

        return {
          matterId: matter.id,
          matterTitle: matter.title,
          matterStatusLabel: matter.status?.label,
          matterStatuteOfLimitations: statute.matterStatuteOfLimitations,
          matterStatuteOfLimitationsConfirmed:
            statute.matterStatuteOfLimitationsConfirmed,
        };
      })
      .filter((row) => {
        if (!row.matterStatuteOfLimitations) {
          return false;
        }

        const statuteDate = dayjs(row.matterStatuteOfLimitations).startOf(
          "day",
        );
        if (!statuteDate.isValid()) {
          return false;
        }

        return !statuteDate.isAfter(windowEndDate);
      })
      .sort((a, b) => {
        const aDate = dayjs(a.matterStatuteOfLimitations);
        const bDate = dayjs(b.matterStatuteOfLimitations);
        return aDate.valueOf() - bDate.valueOf();
      });
  }, [data?.data, selectedWindowDays]);

  return (
    <Card>
      <CardContent className="w-full pt-4">
        <div className="mb-4 flex flex-col gap-3 px-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <Typography variant="heading" color="primary">
              My statutes
            </Typography>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <Select
              value={selectedWindowDays.toString()}
              onValueChange={(value) => {
                setSelectedWindowDays(Number(value));
              }}
            >
              <SelectTrigger
                className="w-[150px]"
                aria-label="Select timeframe"
              >
                <SelectValue placeholder={`Next ${selectedWindowDays} days`} />
              </SelectTrigger>
              <SelectContent>
                {APPROACHING_WINDOW_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option.toString()}>
                    Next {option} days
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DataTable
          entityLabelPlural="matters"
          allowLocalStorage={false}
          hideSearchBar
          hideVisiibilityDropdown
          columns={columns}
          data={rows}
          initialSort={{ id: "Statute of Limitations", desc: false }}
          onRowClick={async (row) => {
            const matterId = row.original.matterId;
            if (!matterId) return;

            await navigate({
              to: "/matters/$matterId",
              params: { matterId },
            });
          }}
          tableName={tanstackTableNames.dashboardApproachingStatutes}
          isLoading={isLoading}
          isError={Boolean(error)}
          noResultsMessage={`No statutes within the next ${selectedWindowDays} days for your matters.`}
        />
      </CardContent>
    </Card>
  );
};
